// src/services/core/HttpClient.ts

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { ApiResponse, ApiError } from './HttpTypes';
import { getAccessToken } from '../../utils/tokenManager';
import Endpoints from '../../constants/endpoints';
import { AppConfig } from '../../utils/appConfig';

const BASE_URL = AppConfig.apiBaseUrl // Or from .env


class HttpClient {
  private client: AxiosInstance;
  constructor() {
    console.log('Initializing HttpClient with base URL:', BASE_URL);
    this.client = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add axios retry for failed requests
    axiosRetry(this.client, {
      retries: 3, // Number of retries
      retryDelay: (retryCount) => {
        console.log(`Retry attempt: ${retryCount}`);
        return retryCount * 1000; // Wait 1s, 2s, 3s before retry
      },
      retryCondition: (error) => {
        // Retry on network errors or 5xx status codes
        return axiosRetry.isNetworkError(error) || axiosRetry.isRetryableError(error);
      },
    });

    // Request interceptor - add token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        const isLoginEndpoint = config.url?.endsWith(Endpoints.LOGIN);
        if (isLoginEndpoint) {
            // add basic auth with username and password
            const username = `${AppConfig.basicAuth.username}`;
            const password = `${AppConfig.basicAuth.password}`;
            const basicAuth = btoa(`${username}:${password}`);
            config.headers['Authorization'] = `Basic ${basicAuth}`;
        } else if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        // Logging only for dev and beta
        if (process.env.REACT_APP_ENV === 'development' || process.env.REACT_APP_ENV === 'beta') {
          console.log(`[Request] ${config.method?.toUpperCase()} ${config.url}`, config);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        if (process.env.REACT_APP_ENV === 'development' || process.env.REACT_APP_ENV === 'beta') {
          console.log(`[Response] ${response.status} ${response.config.url}`, response.data);
        }
        console.log('Response from HttpClient:', response);
        console.log('Response Data from HttpClient:', response.data);
        return response;
      },
      (error: AxiosError) => {
        if (process.env.REACT_APP_ENV === 'development' || process.env.REACT_APP_ENV === 'beta') {
          console.error('[API Error]', error);
        }
        const apiError: ApiError = {
          status: error.response?.status || 500,
          message: error.message,
          details: error.response?.data,
        };
        return Promise.reject(apiError);
      }
    );
  }

  // Same HTTP methods as before
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<T>(url, config);
    return { data: response.data, status: response.status };
  }

  public async post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<T>(url, data, config);
    console.log('Response from HttpClient post:', response);
    console.log('Response Data from HttpClient post:', response.data);
    return { data: response.data, status: response.status};
  }

  public async put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<T>(url, data, config);
    return { data: response.data, status: response.status };
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<T>(url, config);
    return { data: response.data, status: response.status };
  }
}

export default HttpClient;
